import {
  TimeLogCorrectionRequestKind,
  TimeLogCorrectionRequestRecord,
  TimeLogCorrectionRequestStatus
} from '@navio/nsdk';
import type { NoddiAsyncError } from 'noddi-types';
import { type BadgeVariant } from 'noddi-ui-web';
import { formatBusinessTime, NoddiDateFormats } from 'noddi-util';

export const correctionRequestStatusLabel: Record<TimeLogCorrectionRequestStatus, string> = {
  [TimeLogCorrectionRequestStatus.PENDING]: 'Pending',
  [TimeLogCorrectionRequestStatus.APPROVED]: 'Approved',
  [TimeLogCorrectionRequestStatus.REJECTED]: 'Declined',
  [TimeLogCorrectionRequestStatus.WITHDRAWN]: 'Withdrawn',
  // A manager edited the shift directly while this was open, so it no longer
  // applies — distinct from declining it on the merits.
  [TimeLogCorrectionRequestStatus.SUPERSEDED]: 'Superseded'
};

export const correctionRequestStatusVariant = (status: TimeLogCorrectionRequestStatus): BadgeVariant => {
  switch (status) {
    case TimeLogCorrectionRequestStatus.PENDING:
      return 'warning';
    case TimeLogCorrectionRequestStatus.APPROVED:
      return 'success';
    case TimeLogCorrectionRequestStatus.REJECTED:
      return 'destructive';
    default:
      // Withdrawn and superseded are closed without a verdict, so they read
      // neutral rather than borrowing approve/decline color.
      return 'outline';
  }
};

// Business zone, not the browser's: these are payroll numbers, and a manager
// abroad reading a shift shifted by their own offset can approve times neither
// the worker nor the backend meant. Still locale-aware via the house formatter.
const time = (value: string | null | undefined): string =>
  value === null || value === undefined ? '—' : formatBusinessTime(value, NoddiDateFormats.DATE_TIME);

/**
 * The proposal as a manager reads it: only the sides the worker actually asked
 * to move, each as "was -> asked for".
 *
 * A null requested side means "leave this one alone", so it is omitted rather
 * than rendered as an unchanged row — otherwise every request looks like it
 * touches both ends.
 */
export const correctionRequestChanges = (
  request: TimeLogCorrectionRequestRecord
): { label: string; from: string; to: string }[] => {
  if (request.kind === TimeLogCorrectionRequestKind.VOID) {
    return [];
  }
  const changes: { label: string; from: string; to: string }[] = [];
  if (request.requestedClockInAt) {
    changes.push({
      label: 'Clock in',
      from: time(request.observedClockInAt),
      to: time(request.requestedClockInAt)
    });
  }
  if (request.requestedClockOutAt) {
    changes.push({
      label: 'Clock out',
      from: time(request.observedClockOutAt),
      to: time(request.requestedClockOutAt)
    });
  }
  return changes;
};

// The clocked-hours list's correction lens: only an OPEN request earns a badge.
// The list is a pre-payroll sweep for what still needs a decision, and a decided
// request is history the detail page carries.
//
// No filing date in the label: it sits beside the shift's own date column, and
// two dates in one row read as one. The detail card carries "Filed".
export const hasPendingCorrectionRequest = (request: TimeLogCorrectionRequestRecord | null | undefined): boolean =>
  request?.status === TimeLogCorrectionRequestStatus.PENDING;

export interface CorrectionRequestDialogProps {
  request: TimeLogCorrectionRequestRecord | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (decisionNote: string) => void;
  isLoading: boolean;
  // The backend's refusal, rendered inside the dialog. A decision runs a real
  // correction, so it can be refused on grounds the manager could not see up
  // front (an overlapping shift, paid hours, a decided sibling's overtime).
  //
  // Kept under the `mutations` skill's modal exception: the toast auto-dismisses
  // in 3s and an open Radix dialog makes it non-interactive.
  error?: NoddiAsyncError | null;
  workerTo?: string;
}
